import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Save, Loader2, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import CountryCombobox from "@/components/profile/CountryCombobox";

// Business identity fields that surface on every rental agreement. Edit once, applied
// everywhere. Empty inputs clear the field server-side via $unset.
export default function VendorBusinessProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    business_name: "", responsible_person: "",
    business_address: "",
    business_street: "", business_street_number: "", business_city: "", business_zip: "", business_country: "",
    vat_number: "", vat_rate_pct: "", phone: "", whatsapp: "",
    business_email: "", business_logo: "",
    google_place_id: "",
    business_24h: true, business_open_time: "08:00", business_close_time: "20:00",
    business_hours_per_day: null,  // null = use legacy single window / 24h. Array of 7 when set.
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      business_name: user.business_name || "",
      responsible_person: user.responsible_person || "",
      business_address: user.business_address || "",
      business_street: user.business_street || "",
      business_street_number: user.business_street_number || "",
      business_city: user.business_city || "",
      business_zip: user.business_zip || "",
      business_country: user.business_country || "",
      vat_number: user.vat_number || "",
      vat_rate_pct: user.vat_rate_pct != null ? String(user.vat_rate_pct) : "",
      phone: user.phone || "",
      whatsapp: user.whatsapp || "",
      business_email: user.business_email || "",
      business_logo: user.business_logo || "",
      google_place_id: user.google_place_id || "",
      business_24h: user.business_24h === undefined ? true : !!user.business_24h,
      business_open_time: user.business_open_time || "08:00",
      business_close_time: user.business_close_time || "20:00",
      business_hours_per_day: Array.isArray(user.business_hours_per_day) && user.business_hours_per_day.length === 7
        ? user.business_hours_per_day
        : null,
    });
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCountry = (code) => setForm((f) => ({ ...f, business_country: code }));

  const save = async () => {
    setSaving(true);
    try {
      // Mirror the structured address into the legacy single-line `business_address`
      // field so old PDFs / consumers that read the flat string still get the right value.
      const composedAddress = [
        [form.business_street, form.business_street_number].filter(Boolean).join(" "),
        [form.business_zip, form.business_city].filter(Boolean).join(" "),
        form.business_country,
      ].filter((s) => s && s.trim()).join(", ");
      const payload = {
        ...form,
        business_address: composedAddress || form.business_address,
        // When the vendor is 24h, clear the legacy open/close fields server-side so a stale
        // window doesn't accidentally re-apply if they later toggle 24h off.
        business_open_time: form.business_24h || form.business_hours_per_day ? "" : form.business_open_time,
        business_close_time: form.business_24h || form.business_hours_per_day ? "" : form.business_close_time,
        // Per-day overrides the single-window/24h when set.
        business_hours_per_day: form.business_hours_per_day,
      };
      await api.patch("/users/me/business", payload);
      await refresh();
      toast.success("Business profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const pickLogo = () => logoInputRef.current?.click();
  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo too large (max 4MB)"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...form, business_logo: up.data.path };
      setForm(next);
      await api.patch("/users/me/business", { business_logo: up.data.path });
      await refresh();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };
  const removeLogo = async () => {
    if (!form.business_logo) return;
    if (!window.confirm("Remove the logo?")) return;
    try {
      setForm((f) => ({ ...f, business_logo: "" }));
      await api.patch("/users/me/business", { business_logo: "" });
      await refresh();
      toast.success("Logo removed");
    } catch (e) { toast.error("Could not remove"); }
  };

  const logoSrc = form.business_logo ? (form.business_logo.startsWith("http") ? form.business_logo : fileUrl(form.business_logo)) : "";

  return (
    <div className="space-y-6" data-testid="vendor-business-profile">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-neutral-100 flex items-center justify-center"><Building2 className="h-4 w-4" /></div>
        <div>
          <h3 className="font-display text-xl font-medium">Business profile</h3>
          <p className="text-sm text-neutral-500">These details appear on every rental agreement you generate. Keep them current.</p>
        </div>
      </div>

      {/* Logo upload — shown above the form so it's the first thing the vendor sees */}
      <div className="rounded-2xl border border-border bg-white p-6 flex items-center gap-5 flex-wrap" data-testid="vendor-logo-card">
        <div className="h-24 w-24 rounded-2xl border-2 border-dashed border-border bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0">
          {logoSrc ? (
            <img src={logoSrc} alt="Business logo" className="h-full w-full object-contain" data-testid="vendor-logo-preview" />
          ) : (
            <ImagePlus className="h-7 w-7 text-neutral-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">Business logo</div>
          <p className="text-xs text-neutral-500 mt-0.5 max-w-md">Shown on car listings, rental agreements, and your public vendor chip. Square images work best — PNG with transparent background is ideal. Max 4 MB.</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button type="button" size="sm" variant="ghost" onClick={pickLogo} disabled={uploadingLogo} className="rounded-full border border-border" data-testid="vendor-logo-upload">
              {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5 mr-1.5" />}
              {logoSrc ? "Replace logo" : "Upload logo"}
            </Button>
            {logoSrc && (
              <Button type="button" size="sm" variant="ghost" onClick={removeLogo} className="rounded-full text-rose-600 hover:text-rose-700" data-testid="vendor-logo-remove">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove
              </Button>
            )}
          </div>
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoFile} data-testid="vendor-logo-input" />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Business name</Label>
          <Input className="mt-1 rounded-xl" value={form.business_name} onChange={set("business_name")} placeholder="e.g. V1 Cars LLC" data-testid="biz-business-name" />
        </div>
        <div>
          <Label>Responsible person <span className="text-neutral-400 font-normal">(legal contact)</span></Label>
          <Input className="mt-1 rounded-xl" value={form.responsible_person} onChange={set("responsible_person")} placeholder="e.g. Anna Müller" data-testid="biz-responsible-person" />
        </div>
        <div>
          <Label>VAT / NIPT / NIF number</Label>
          <Input className="mt-1 rounded-xl" value={form.vat_number} onChange={set("vat_number")} placeholder="e.g. NIPT-K47912345A" data-testid="biz-vat" />
        </div>
        <div>
          <label className="text-sm font-medium">Default VAT / Tax rate (%)</label>
          <Input type="number" inputMode="decimal" className="mt-1 rounded-xl" value={form.vat_rate_pct} onChange={set("vat_rate_pct")} placeholder="e.g. 20" data-testid="biz-vat-rate" />
          <p className="text-xs text-neutral-500 mt-1">Pre-filled on manual bookings; leave empty for no VAT line.</p>
        </div>
        <div>
          <Label>Business email</Label>
          <Input type="email" className="mt-1 rounded-xl" value={form.business_email} onChange={set("business_email")} placeholder="bookings@yourcompany.com" data-testid="biz-email" />
        </div>

        {/* Iter 67ab — Google Business Profile Place ID. When set, the platform
            augments in-platform reviews with live Google reviews on the
            vendor's car pages and contributes them to the home-page social-proof
            block. Find the ID via Google Maps → search → URL contains `!1s<id>`,
            or via the official tool at developers.google.com/maps/.../place-id. */}
        <div className="md:col-span-2">
          <Label className="flex items-center gap-2">
            Google Business Profile <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100 font-semibold">optional</span>
          </Label>
          <Input
            className="mt-1 rounded-xl font-mono text-sm"
            value={form.google_place_id}
            onChange={set("google_place_id")}
            placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
            data-testid="biz-google-place-id"
          />
          <p className="text-[11px] text-neutral-500 mt-1.5 leading-relaxed">
            Paste your Google Business Profile <strong>Place ID</strong> to surface live Google reviews on your car pages and the homepage. Find it at{" "}
            <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">developers.google.com</a>.
          </p>
        </div>

        {/* Structured business address */}
        <div className="md:col-span-2 pt-2">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Registered address</div>
        </div>
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-[1fr,120px] gap-3">
          <div>
            <Label>Street</Label>
            <Input className="mt-1 rounded-xl" value={form.business_street} onChange={set("business_street")} placeholder="e.g. Rruga Skënderbeu" data-testid="biz-street" />
          </div>
          <div>
            <Label>Number</Label>
            <Input className="mt-1 rounded-xl" value={form.business_street_number} onChange={set("business_street_number")} placeholder="42" data-testid="biz-street-number" />
          </div>
        </div>
        <div className="grid grid-cols-[1fr,120px] gap-3">
          <div>
            <Label>City</Label>
            <Input className="mt-1 rounded-xl" value={form.business_city} onChange={set("business_city")} placeholder="e.g. Tirana" data-testid="biz-city" />
          </div>
          <div>
            <Label>ZIP</Label>
            <Input className="mt-1 rounded-xl" value={form.business_zip} onChange={set("business_zip")} placeholder="1001" data-testid="biz-zip" />
          </div>
        </div>
        <div>
          <Label>Country</Label>
          <div className="mt-1">
            <CountryCombobox
              value={form.business_country || ""}
              onChange={setCountry}
              placeholder="Pick a country…"
              testId="biz-country"
            />
          </div>
        </div>

        <div>
          <Label>Phone</Label>
          <Input className="mt-1 rounded-xl" value={form.phone} onChange={set("phone")} placeholder="+355 69 555 0000" data-testid="biz-phone" />
        </div>
        <div>
          <Label>WhatsApp</Label>
          <Input className="mt-1 rounded-xl" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+355 69 555 0000" data-testid="biz-whatsapp" />
        </div>

        {/* Operating hours — blocks bookings whose pickup/return time falls outside the window. */}
        <div className="md:col-span-2 pt-3" data-testid="biz-hours-section">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Operating hours</div>
          <p className="text-xs text-neutral-500 mb-3">
            Customers can only request pickup/return times when you're open. Pick "24h" for round-the-clock self-service, or "Per day" to set different hours for each weekday (e.g. closed on Sundays).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, business_24h: true, business_hours_per_day: null }))}
              className={`rounded-full px-4 py-2 text-sm border transition ${form.business_24h && !form.business_hours_per_day ? "bg-blue-600 text-white border-blue-600" : "bg-white border-border hover:bg-neutral-50"}`}
              data-testid="biz-hours-24h"
            >
              24 h · always open
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, business_24h: false, business_hours_per_day: null }))}
              className={`rounded-full px-4 py-2 text-sm border transition ${!form.business_24h && !form.business_hours_per_day ? "bg-blue-600 text-white border-blue-600" : "bg-white border-border hover:bg-neutral-50"}`}
              data-testid="biz-hours-window"
            >
              Single daily window
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({
                ...f,
                business_24h: false,
                business_hours_per_day: f.business_hours_per_day || Array.from({ length: 7 }, () => ({
                  is_closed: false, is_24h: false,
                  open_time: f.business_open_time || "09:00",
                  close_time: f.business_close_time || "18:00",
                })),
              }))}
              className={`rounded-full px-4 py-2 text-sm border transition ${form.business_hours_per_day ? "bg-blue-600 text-white border-blue-600" : "bg-white border-border hover:bg-neutral-50"}`}
              data-testid="biz-hours-per-day"
            >
              Per day
            </button>
          </div>

          {/* Single-window editor */}
          {!form.business_24h && !form.business_hours_per_day && (
            <div className="grid grid-cols-2 gap-3 mt-3 max-w-md">
              <div>
                <Label className="text-xs">Open from</Label>
                <Input
                  type="time"
                  className="mt-1 rounded-xl"
                  value={form.business_open_time}
                  onChange={set("business_open_time")}
                  data-testid="biz-open-time"
                />
              </div>
              <div>
                <Label className="text-xs">Open until</Label>
                <Input
                  type="time"
                  className="mt-1 rounded-xl"
                  value={form.business_close_time}
                  onChange={set("business_close_time")}
                  data-testid="biz-close-time"
                />
              </div>
              <p className="col-span-2 text-[11px] text-neutral-500">
                Overnight windows (e.g. 22:00 → 06:00) wrap correctly. Set close ≤ open if you only operate overnight.
              </p>
            </div>
          )}

          {/* Per-day editor */}
          {form.business_hours_per_day && (
            <div className="mt-4 rounded-2xl border border-border bg-neutral-50/40 p-3" data-testid="biz-hours-per-day-editor">
              <div className="grid grid-cols-[110px_auto_auto_1fr_1fr] gap-2 items-center text-xs text-neutral-500 font-medium uppercase tracking-wider mb-2 px-2">
                <div>Day</div>
                <div className="text-center">Closed</div>
                <div className="text-center">24h</div>
                <div>Open</div>
                <div>Close</div>
              </div>
              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((dayName, idx) => {
                const day = form.business_hours_per_day[idx] || { is_closed: false, is_24h: false, open_time: "09:00", close_time: "18:00" };
                const updateDay = (patch) => setForm((f) => ({
                  ...f,
                  business_hours_per_day: f.business_hours_per_day.map((d, i) => i === idx ? { ...d, ...patch } : d),
                }));
                const disabled = day.is_closed || day.is_24h;
                return (
                  <div key={idx} className="grid grid-cols-[110px_auto_auto_1fr_1fr] gap-2 items-center px-2 py-1.5 rounded-xl hover:bg-white" data-testid={`biz-hours-day-${idx}`}>
                    <div className="text-sm font-medium">{dayName}</div>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={day.is_closed}
                        onChange={(e) => updateDay({ is_closed: e.target.checked, is_24h: e.target.checked ? false : day.is_24h })}
                        className="h-4 w-4 rounded border-input"
                        data-testid={`biz-hours-day-${idx}-closed`}
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={day.is_24h}
                        onChange={(e) => updateDay({ is_24h: e.target.checked, is_closed: e.target.checked ? false : day.is_closed })}
                        className="h-4 w-4 rounded border-input"
                        data-testid={`biz-hours-day-${idx}-24h`}
                      />
                    </div>
                    <Input
                      type="time"
                      disabled={disabled}
                      value={day.open_time || ""}
                      onChange={(e) => updateDay({ open_time: e.target.value })}
                      className="rounded-lg h-9"
                      data-testid={`biz-hours-day-${idx}-open`}
                    />
                    <Input
                      type="time"
                      disabled={disabled}
                      value={day.close_time || ""}
                      onChange={(e) => updateDay({ close_time: e.target.value })}
                      className="rounded-lg h-9"
                      data-testid={`biz-hours-day-${idx}-close`}
                    />
                  </div>
                );
              })}
              <div className="flex justify-end gap-2 mt-3 px-2">
                <button
                  type="button"
                  onClick={() => {
                    const first = form.business_hours_per_day[0];
                    setForm((f) => ({ ...f, business_hours_per_day: f.business_hours_per_day.map(() => ({ ...first })) }));
                  }}
                  className="text-xs text-blue-600 hover:underline"
                  data-testid="biz-hours-copy-monday"
                >
                  Copy Monday → all days
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-2 flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="biz-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save changes
          </Button>
        </div>
      </div>

      {/* Permanent signature — applied by default to every agreement we generate */}
      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Signature on file</Label>
            <p className="text-xs text-neutral-500 mt-1">
              Stored on your profile and pre-filled on every rental agreement. You can still sign individual agreements with a different stroke if needed.
            </p>
          </div>
        </div>
        <BusinessSignatureBlock />
      </div>
    </div>
  );
}


function BusinessSignatureBlock() {
  const { user, refresh } = useAuth();
  const [drawing, setDrawing] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/signature", { image: drawing });
      await refresh();
      toast.success(drawing ? "Signature saved" : "Signature cleared");
      setDrawing("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <SignaturePad
        value={drawing || user?.signature_image || ""}
        onChange={setDrawing}
        testId="biz-signature"
      />
      <div className="flex items-center justify-end gap-2">
        {user?.signature_image && (
          <Button
            type="button"
            variant="ghost"
            onClick={async () => {
              setSaving(true);
              try { await api.patch("/users/me/signature", { image: "" }); await refresh(); toast.success("Signature cleared"); }
              catch (e) { toast.error("Could not clear"); }
              finally { setSaving(false); }
            }}
            className="rounded-full text-rose-600 hover:text-rose-700"
            data-testid="biz-signature-remove"
          >
            Remove saved signature
          </Button>
        )}
        <Button onClick={save} disabled={saving || !drawing} className="rounded-full" data-testid="biz-signature-save">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save signature
        </Button>
      </div>
    </div>
  );
}
